import type { BoardJournalEntry } from "@/hooks/useBoardJournal";
import { DECKS, type DeckId } from "@/data/recovery-board-cards";

const fmt = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

/** Private journey log saved to the member's account. */
export function BoardJourney({ entries, failed }: { entries: BoardJournalEntry[]; failed: boolean }) {
  return (
    <section aria-labelledby="rb-journey" className="mt-4 w-full max-w-md rounded-2xl border bg-card p-4 text-card-foreground sm:max-w-2xl">
      <h2 id="rb-journey" className="font-serif text-lg">📖 My board journey</h2>
      <p className="text-sm text-muted-foreground">Every space you land on and every card you draw is saved privately to your account.</p>
      {failed && <p role="status" className="mt-2 text-sm text-destructive">Your last step couldn't be saved — please check your connection.</p>}
      {entries.length === 0 ? (
        <p className="mt-3 text-sm">Roll the dice to start your journey.</p>
      ) : (
        <ul className="mt-3 max-h-72 space-y-2 overflow-y-auto pr-1">
          {entries.map((e) => {
            const d = e.deck ? DECKS[e.deck as DeckId] : null;
            return (
              <li key={e.id} className="rounded-xl border bg-background p-3 text-sm">
                <div className="flex justify-between gap-2 text-xs text-muted-foreground">
                  <span>{e.kind === "space" ? "Landed on" : `${d?.emoji ?? "🃏"} ${d?.name ?? "Card"} · ${e.outcome === "skipped" ? "skipped gently" : "taken"}`}</span>
                  <time dateTime={e.created_at}>{fmt(e.created_at)}</time>
                </div>
                <p className="mt-1 font-medium text-foreground">{e.kind === "space" ? e.space_title : e.card_text}</p>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
